import { formatMoney, getAccountMoney, moneyFromMinor } from '../../../shared/money/exact-money.js';
function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function formatPersonName(person) {
  if (!person || typeof person !== 'object') {
    return '-';
  }

  const firstName = pickFirst(person.firstName, person.first_name);
  const lastName = pickFirst(person.lastName, person.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || pickFirst(person.name, person.full_name, person.email, person.uid, person.id) || '-';
}

function extractCountry(profile) {
  return pickFirst(
    profile?.country,
    profile?.countryName,
    profile?.country_name,
    profile?.countryCode,
    profile?.country_code,
  ) || '-';
}

function extractMoneyAmountMinor(money) {
  const rawAmount = money?.amount;
  if (rawAmount === undefined || rawAmount === null || String(rawAmount).trim() === '') {
    return '';
  }

  return String(rawAmount).trim();
}

function normalizeString(value) {
  return String(value ?? '').trim();
}

function normalizeStringOrDash(value) {
  return pickFirst(value) || '-';
}

function normalizeArray(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeAccount(account, locale) {
  const source = account && typeof account === 'object' ? account : {};

  return {
    id: normalizeString(pickFirst(source.uid, source.id)),
    type: normalizeStringOrDash(source.type),
    login: normalizeStringOrDash(source.login),
    currency: normalizeStringOrDash(source.currency),
    tradingServerId: normalizeString(pickFirst(source.tradingServerId, source.trading_server_id)),
    platform: normalizeStringOrDash(source.platform),
    balanceMoney: getAccountMoney(source),
    balance: formatMoney(getAccountMoney(source), locale, { fallback: '-', trimTrailingZeros: true }),
  };
}

function normalizeInteger(value) {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }

  const numberValue = Number(value);
  return Number.isInteger(numberValue) ? numberValue : null;
}

function normalizeCalculation(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const sourceCurrency = normalizeString(value.sourceCurrency).toUpperCase();
  const targetCurrency = normalizeString(value.targetCurrency).toUpperCase();
  const sourceAmountMinor = moneyFromMinor({ amount: value.sourceAmountMinor, currency: sourceCurrency })?.units ?? null;
  const grossTargetAmountMinor = moneyFromMinor({ amount: value.grossTargetAmountMinor, currency: targetCurrency })?.units ?? null;
  const feeAmountMinor = moneyFromMinor({ amount: value.feeAmountMinor, currency: targetCurrency })?.units ?? null;
  const netTargetAmountMinor = moneyFromMinor({ amount: value.netTargetAmountMinor, currency: targetCurrency })?.units ?? null;

  if (!sourceCurrency || !targetCurrency || sourceAmountMinor === null) {
    return null;
  }

  return {
    sourceCurrency,
    targetCurrency,
    sourceAmountMinor,
    grossTargetAmountMinor,
    feeAmountMinor,
    netTargetAmountMinor,
    baseRate: normalizeString(value.baseRate),
    feePips: normalizeInteger(value.feePips),
    defaultFeePips: normalizeInteger(value.defaultFeePips),
    usingDefaultFeePips: Boolean(value.usingDefaultFeePips),
    pipSize: normalizeString(value.pipSize),
    effectiveRate: normalizeString(value.effectiveRate),
    roundingMode: normalizeString(value.roundingMode),
  };
}

function normalizeDetailsList(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === 'object');
  }

  if (value && typeof value === 'object') {
    return Object.values(value).filter((item) => item && typeof item === 'object');
  }

  return [];
}

function extractPaymentDetails(transaction) {
  const paymentDetails = normalizeDetailsList(transaction?.paymentDetails);
  if (paymentDetails.length > 0) {
    return {
      source: 'display',
      rows: paymentDetails,
    };
  }

  const details = transaction?.details;

  if (!details || typeof details !== 'object') {
    return {
      source: 'request',
      rows: [],
    };
  }

  return {
    source: 'request',
    rows: normalizeDetailsList(
      details.createForm
      || details.create
      || details.paymentRequest
      || details.fields
      || details,
    ),
  };
}

export function mapTransactionDetailsResponseToDto(payload, locale) {
  const transaction = payload?.transaction;
  if (!transaction || typeof transaction !== 'object') {
    return null;
  }

  const paymentDetails = extractPaymentDetails(transaction);
  const sourceAccount = normalizeAccount(transaction.account, locale);
  const targetAccount = normalizeAccount(transaction.accountIdOpt, locale);

  return {
    id: normalizeString(pickFirst(transaction.uid, transaction.id)),
    parentTransactionId: normalizeString(transaction.parentTransactionId),
    childTransactionIds: normalizeArray(transaction.childTransactionIds).map(normalizeString).filter(Boolean),
    profileId: normalizeString(pickFirst(transaction?.profile?.uid, transaction?.profile?.id)),
    profileName: formatPersonName(transaction?.profile),
    profileCountry: extractCountry(transaction?.profile),
    profileStatus: pickFirst(transaction?.profile?.status) || '-',
    type: pickFirst(transaction.type) || '-',
    methodId: pickFirst(transaction.methodId, transaction.method_id) || '-',
    amountMinor: extractMoneyAmountMinor(transaction.money),
    currency: pickFirst(transaction?.money?.currency?.code, transaction?.money?.currency) || '-',
    status: pickFirst(transaction.status) || '-',
    reference: pickFirst(transaction.reference) || '-',
    accountId: sourceAccount.id || targetAccount.id,
    accountType: sourceAccount.type !== '-' ? sourceAccount.type : targetAccount.type,
    accountLogin: sourceAccount.login !== '-' ? sourceAccount.login : targetAccount.login,
    accountCurrency: sourceAccount.currency !== '-' ? sourceAccount.currency : targetAccount.currency,
    tradingServerId: sourceAccount.tradingServerId || targetAccount.tradingServerId,
    platform: sourceAccount.platform !== '-' ? sourceAccount.platform : targetAccount.platform,
    accountBalance: sourceAccount.balance,
    accountBalanceMoney: sourceAccount.balanceMoney,
    accountOrderId: pickFirst(transaction?.accountOrderId, transaction?.accountOptOrderId) || '-',
    sourceAccount,
    targetAccount,
    sourceAccountOrderId: pickFirst(transaction?.accountOrderId) || '-',
    targetAccountOrderId: pickFirst(transaction?.accountOptOrderId) || '-',
    ownTransferCalculation: normalizeCalculation(transaction?.details?.ownTransferCalculation),
    paymentDetails: paymentDetails.rows,
    paymentDetailsSource: paymentDetails.source,
    paymentExecution: transaction.paymentExecution?.version === '2' ? transaction.paymentExecution : null,
    source: transaction,
  };
}
